import React, { useState, useRef, useEffect } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  containerClassName = '',
  placeholderClassName = 'bg-gray-100 text-gray-400',
  iconClassName = 'w-8 h-8',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  const isValidSrc = src && typeof src === 'string' && src.trim() !== '';

  useEffect(() => {
    if (isValidSrc) {
      setIsLoaded(false);
      setHasError(false);
    }
  }, [src, isValidSrc]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true); // Hide placeholder even on error to prevent ugly red box, but maybe show icon?
  };

  return (
    <div className={`relative overflow-hidden ${containerClassName || className}`}>
      
      {/* Placeholder Layer - Only shown if NOT loaded OR has error */}
      {(!isLoaded || hasError || !isValidSrc) && (
        <div className={`absolute inset-0 z-10 flex items-center justify-center ${placeholderClassName}`}>
          {isValidSrc && !hasError ? (
            <div className="animate-pulse">
              <PhotoIcon className={iconClassName} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
               <PhotoIcon className={iconClassName} />
            </div>
          )}
        </div>
      )}
      
      {/* Image Layer - Always rendered if valid src, opacity always 1 to ensure visibility if loaded */}
      {isValidSrc && (
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={`${className}`} 
          style={{ opacity: 1, display: 'block' }} // Force display block and opacity 1
          {...props}
        />
      )}
    </div>
  );
};

export default LazyImage;
